import {
  AssetType,
  BatteryDataType,
  GasProductionDataType,
  GasStorageDataType,
  itemsFilteredByGroup,
  MotorDataType,
  OptimizationGasStorage,
  OptimizationGroupParametersType,
  OptimizationTargetRatedPowerGroup,
  PVSystemDataType,
} from '@e2m-customer-portal/domains';
import { MarketLocation, useLocale, useOptimizationGroupData } from '@e2m-customer-portal/hooks';
import { Nullable } from '@e2m-customer-portal/shared';
import { Loading } from '@e2m-customer-portal/ui';
import { Button, IconButton, Tooltip } from '@mui/material';
import { ArrowForwardIosIcon, BinIcon, EditIcon, InfoIcon } from 'assets/icons';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';

interface OptimizationGroupProps {
  optimizationGroupId: string;
  marketLocations: Array<MarketLocation>;
  startExpanded?: boolean;
}

export function OptimizationGroup({
  optimizationGroupId,
  marketLocations,
  startExpanded = false,
}: Readonly<OptimizationGroupProps>): ReactElement<OptimizationGroupProps> {
  const { code: locale } = useLocale();
  const [t] = useTranslation();
  const [expanded, toggleExpanded] = useToggle(startExpanded);

  const { data, loadingData } = useOptimizationGroupData(optimizationGroupId);

  const [optimizationGroupParametersData, setOptimizationGroupParametersData] =
    useState<Nullable<OptimizationGroupParametersType>>(undefined);
  const [gasStorageData, setGasStorageData] = useState<Nullable<GasStorageDataType>>(undefined);
  const [gasProductionData, setGasProductionData] = useState<Nullable<GasProductionDataType>>(undefined);
  const [PVSystemData, setPVSystemData] = useState<Nullable<PVSystemDataType>>(undefined);
  const [PVStandAloneData, setPVStandAloneData] = useState<Nullable<PVSystemDataType[]>>(undefined);
  const [batteriesData, setBatteriesData] = useState<Nullable<Map<string, BatteryDataType>>>(undefined);
  const [motorsData, setMotorsData] = useState<Nullable<Map<string, MotorDataType>>>(undefined);

  useEffect(() => {
    if (data?.json) {
      const configuration = JSON.parse(data.json);
      setOptimizationGroupParametersData(configuration);
      switch (configuration.asset_type) {
        case AssetType.InnoTender:
        case AssetType.InnoTenderGE:
          setPVSystemData(configuration.pv);
          setBatteriesData(new Map(Object.entries(configuration.batteries)));
          return;
        case AssetType.CoLocation:
          setPVStandAloneData(configuration.pv);
          setBatteriesData(new Map(Object.entries(configuration.batteries)));
          return;
        case AssetType.Biogas:
          setGasStorageData(configuration.storage);
          setGasProductionData(configuration.gas_production);
          setMotorsData(new Map(Object.entries(configuration.motors)));
          return;
        case AssetType.PeakShavingBatteries:
        case AssetType.FleximumBatteries:
        case AssetType.AFRRBatteries:
          setBatteriesData(new Map(Object.entries(configuration.batteries)));
          return;
        case AssetType.Danpower:
          setOptimizationGroupParametersData(configuration);
      }
    }
  }, [data, optimizationGroupId]);

  if (loadingData)
    return (
      <section className="pt-2 bg-white">
        <Loading />
      </section>
    );

  return (
    <section className="flex flex-col gap-y-14 py-2 bg-white shadow-sm">
      <header className="flex gap-x-1">
        <IconButton className="ml-3 px-3 p-0 rounded-md" onClick={toggleExpanded}>
          <ArrowForwardIosIcon className={clsx('transition-transform rotate-90', { '-scale-x-100': expanded })} />
        </IconButton>
        <div className="flex gap-y-2 flex-wrap *:flex *:flex-col *:justify-end *:gap-y-1 first:*:pl-0 *:px-6 last:*:border-none *:border-solid *:border-0 *:border-r *:border-grey-200">
          {optimizationGroupParametersData?.name && (
            <div>
              <h2 className="m-0 font-bold text-base leading-5">{optimizationGroupParametersData.name}</h2>
              <span className="text-xs leading-4 text-grey-400">
                {t('OPTIMIZATION_GROUP_ID', { optimizationGroupId })}
              </span>
            </div>
          )}
          {optimizationGroupParametersData?.asset_type && (
            <div>
              <span className="text-xs leading-4 text-grey-300">{t('OPTIMIZATION_ASSET_TYPE')}</span>
              <span className="font-semibold text-sm leading-4">{optimizationGroupParametersData.asset_type}</span>
            </div>
          )}
          {optimizationGroupParametersData?.status && (
            <div>
              <span className="text-xs leading-4 text-grey-300">{t('OPTIMIZATION_STATUS')}</span>
              <span className="inline-flex items-center gap-x-1 font-semibold text-sm leading-4 text-divider">
                TBD ({optimizationGroupParametersData.status})
                <Tooltip title="Noch nicht implementiert." placement="right">
                  <InfoIcon className="size-4 text-divider" />
                </Tooltip>
              </span>
            </div>
          )}
          {optimizationGroupParametersData?.valid_from_timestamp && (
            <div>
              <span className="text-xs leading-4 text-grey-300">{t('OPTIMIZATION_VALID_FROM')}</span>
              <span className="font-semibold text-sm leading-4">
                {parseISO(optimizationGroupParametersData.valid_from_timestamp).toLocaleDateString(locale)}
              </span>
            </div>
          )}
          {expanded && (
            <>
              <div>
                <Button disabled startIcon={<EditIcon />} variant="outlined">
                  {t('SHARED_EDIT')}
                </Button>
              </div>
              <div>
                <Button disabled startIcon={<BinIcon />} variant="outlined" color="error">
                  {t('SHARED_CLEAR')}
                </Button>
              </div>
            </>
          )}
        </div>
      </header>
      {expanded && motorsData && (
        <div className="relative self-center flex flex-col md:flex-row items-center md:items-start gap-4 *:shrink-0 py-6 mx-4 box-border overflow-x-auto">
          <OptimizationGasStorage gasStorageData={gasStorageData} gasProductionData={gasProductionData} />
          {optimizationGroupParametersData?.target_rated_power_groups?.map((groupData, index) => (
            <OptimizationTargetRatedPowerGroup
              key={groupData.identifier}
              index={index}
              data={groupData}
              items={itemsFilteredByGroup(motorsData, groupData.name)}
              marketLocations={marketLocations}
            />
          ))}
          <div className="hidden md:block absolute top-0 left-32 right-[6.5rem] h-6 border-dashed border border-b-0 rounded-t border-blue-grey-100" />
          <Button className="w-52" disabled variant="outlined">
            {t('OPTIMIZATION_TARGET_RATED_POWER_GROUP_ADD_GROUP')}
          </Button>
        </div>
      )}
    </section>
  );
}
